<template>
  <button
    class="btn"
    :class="[btnClasses]"
    :disabled="disabled || loading"
    @mouseover="hovered = true"
    @mouseleave="hovered = false"
    @click="handleClick"
  >
    <Icon
      v-if="loading && (!iconDirection || iconDirection === 'left')"
      icon="eos-icons:bubble-loading"
      :width="iconSizeClasses"
    ></Icon>
    <Icon
      v-if="icon && !loading && (!iconDirection || iconDirection === 'left')"
      :icon="icon.icon"
      :color="icon.color ?? iconColorClasses"
      :width="icon.width ?? iconSizeClasses"
    />
    <img v-if="image && !loading && (!iconDirection || iconDirection === 'left')" :src="image" />
    <p v-if="text && !loading">
      {{ text }}
    </p>

    <Icon
      v-if="loading && iconDirection === 'right'"
      icon="eos-icons:bubble-loading"
      :width="iconSizeClasses"
    ></Icon>
    <Icon
      v-if="icon && !loading && iconDirection === 'right'"
      :icon="icon.icon"
      :color="icon.color ?? iconColorClasses"
      :width="icon.width ?? iconSizeClasses"
    />
    <img v-if="image && !loading && iconDirection === 'right'" :src="image" />
  </button>
</template>

<script setup lang="ts">
import Icon from '@/shared/ui/components/general/Icon.vue'
import { computed, ref } from 'vue'

type ButtonType = 'primary' | 'primary-fill' | 'tertiary' | 'clear'

interface Props {
  type: ButtonType
  text?: string
  icon?: Icon
  image?: string
  iconDirection?: 'left' | 'right'
  size?: '2xl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs'
  disabled?: boolean
  loading?: boolean
  active?: boolean
  dark?: boolean
}

const props = defineProps<Props>()
const emits = defineEmits(['click'])
props.size

const hovered = ref<boolean>(false)

const handleClick = (e: MouseEvent) => {
  if (props.disabled || props.loading) return
  emits('click', e)
}

const stateClass = computed(() =>
  props.disabled
    ? 'disabled'
    : props.loading
      ? 'loading'
      : props.active
        ? 'active'
        : hovered.value
          ? 'hover'
          : 'default'
)

const iconColorClasses = computed(() => {
  const typeMap: Record<ButtonType, Record<string, string>> = {
    primary: {
      default: props.dark ? '#03C75A' : '#00B14F',
      hover: props.dark ? '#03C75A' : '#00B14F',
      press: props.dark ? '#03C75A' : '#00B14F',
      loading: props.dark ? '#03C75A' : '#00B14F',
      disabled: props.dark ? '#D0D0D0' : '#E0E5E3'
    },
    'primary-fill': {
      default: props.dark ? '#161817' : '#FFFFFF',
      hover: props.dark ? '#161817' : '#FFFFFF',
      press: props.dark ? '#161817' : '#FFFFFF',
      loading: props.dark ? '#161817' : '#FFFFFF',
      disabled: props.dark ? '#D0D0D0' : '#D0D0D0'
    },
    tertiary: {
      default: props.dark ? '#8B9892' : '#5B625F',
      hover: props.dark ? '#8B9892' : '#5B625F',
      press: props.dark ? '#8B9892' : '#5B625F',
      loading: props.dark ? '#8B9892' : '#5B625F',
      disabled: props.dark ? '#D0D0D0' : '#E0E5E3'
    },
    clear: {
      default: '',
      hover: '',
      press: '',
      loading: '',
      disabled: ''
    }
  }

  return typeMap[props.type][stateClass.value]
})

const iconSizeClasses = computed(() => {
  const sizeMap: Record<'2xl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs', string> = {
    '2xl': '24',
    xl: '24',
    lg: '24',
    md: '20',
    sm: '20',
    xs: '20'
  }
  return sizeMap[props.size || 'md']
})

const btnClasses = computed(() => {
  const baseClass =
    'cursor-pointer flex items-center justify-center gap-2 transition-2 whitespace-nowrap'

  const sizeMap: Record<'2xl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs', string> = {
    '2xl': 'px-12 h-32 heading-small rounded-lg',
    xl: 'px-12 h-27 text-medium rounded-lg',
    lg: 'px-8 h-24 text-medium-bold rounded-lg',
    md: 'px-8 h-20 text-small-bold rounded-lg',
    sm: 'px-6 h-18 text-xsmall rounded-lg',
    xs: 'px-4 h-15 text-xsmall rounded-lg'
  }

  if (props.type === 'clear') {
    return [baseClass, 'p-0 m-0 border-0 bg-white rounded-0', sizeMap[props.size || 'md']]
  }

  const typeMap: Record<ButtonType, Record<string, string>> = {
    primary: {
      default: props.dark
        ? 'text-primary-40 bg-white/0 border border-primary-40'
        : 'text-primary-50 bg-white/0 border  border-primary-50',
      hover: 'text-primary-50 bg-black/10 border border-primary-50-10',
      press: 'text-primary-50 bg-black/25 border border-primary-50-25',
      loading: 'text-primary-50 bg-black/25 border border-primary-50-25',
      disabled: 'text-gray-20 bg-white border border-gray-30'
    },
    'primary-fill': {
      default: props.dark ? 'text-gray-95 bg-primary-40' : 'text-white bg-primary-50',
      hover: props.dark ? 'text-gray-95 bg-primary-50-10' : 'text-white bg-primary-50-10',
      press: props.dark ? 'text-gray-95 bg-primary-50-25' : 'text-white bg-primary-50-25',
      loading: props.dark ? 'text-gray-95 bg-primary-50-25' : 'text-white bg-primary-50-25',
      disabled: props.dark ? 'text-white bg-gray-20' : 'text-white bg-gray-20'
    },
    tertiary: {
      default: props.dark
        ? 'text-gray-30 bg-black/0 border border-gray-70'
        : 'text-gray-70 bg-black/0 border border-gray-20',
      hover: props.dark
        ? 'text-gray-30 bg-black/10 border border-gray-70'
        : 'text-gray-70 bg-black/10 border border-gray-20-10',
      press: props.dark
        ? 'text-gray-30 bg-black/25 border border-gray-70'
        : 'text-gray-70 bg-black/25 border border-gray-20-25',
      loading: props.dark
        ? 'text-gray-30 bg-black/25 border border-gray-70'
        : 'text-gray-70 bg-black/25 border border-gray-20-25',
      disabled: props.dark
        ? 'text-gray-20 bg-black/0 border border-gray-70'
        : 'text-gray-20 bg-black/0 border border-gray-20'
    },
    clear: {
      default: '',
      hover: '',
      press: '',
      loading: '',
      disabled: ''
    }
  }

  return [baseClass, typeMap[props.type][stateClass.value], sizeMap[props.size || 'md']].join(' ')
})
</script>
